const defaultObjectIndentSize = 2
const convertErrorHintText = 'internal "convertObjectToString" error:'
const convertErrorDescriptionText =
  'possible circular structure (if following error is not regarding a circular structure please adjust error handling)'
const convertErrorFallbackText =
  'no specific error handling, adjust error handling for more details'

const customErrorNamePrefix = 'Custom'

interface CustomErrorOptions<CustomErrorType extends Error> {
  cause?: CustomErrorType | undefined
}

class CustomError<CustomErrorType extends Error> extends Error {
  public override readonly cause?: CustomErrorType | undefined
  public constructor(
    customErrorNameSuffix?: string,
    customErrorMessage?: string,
    customErrorCause?: CustomErrorType,
  ) {
    const currentErrorNameSuffix = customErrorNameSuffix ?? ''
    const customErrorOptions: CustomErrorOptions<CustomErrorType> = {
      cause: customErrorCause,
    }

    super(customErrorMessage, customErrorOptions)
    this.name = customErrorNamePrefix.concat(currentErrorNameSuffix)
    this.cause = customErrorOptions.cause
  }
}

type ConvertObjectToStringFunction = (unknownObject: unknown, objectIndentSize: number) => string

const convertObjectToString: ConvertObjectToStringFunction = (unknownObject, objectIndentSize) => {
  try {
    return JSON.stringify(unknownObject, undefined, objectIndentSize)
  } catch (objectError) {
    console.error(convertErrorHintText)

    if (objectError instanceof TypeError) {
      console.error(convertErrorDescriptionText)
      console.error(objectError.message)
    } else {
      console.error(convertErrorFallbackText)
    }

    return ''
  }
}

const convertUnknownErrorObjectToString = (
  unknownErrorObject: unknown,
  objectIndentSize?: number,
): string => {
  const currentObjectIndentSize = objectIndentSize ?? defaultObjectIndentSize

  if (unknownErrorObject instanceof Function) {
    const functionErrorPrefix = 'FunctionObject: '
    const functionNameErrorString = convertObjectToString(
      unknownErrorObject.name,
      currentObjectIndentSize,
    )
    const functionErrorString = functionErrorPrefix.concat(functionNameErrorString)

    return functionErrorString
  }

  return convertObjectToString(unknownErrorObject, currentObjectIndentSize)
}

const handleUnknownError = (unknownErrorObject: unknown): void => {
  const errorInfoPrefix = 'unknown error, no error object: '
  const errorObjectString = convertUnknownErrorObjectToString(unknownErrorObject)

  console.error(errorInfoPrefix, errorObjectString)
}

export {
  convertUnknownErrorObjectToString,
  CustomError,
  type CustomErrorOptions,
  handleUnknownError,
}
